import { motion } from "framer-motion";
import {
  BookOpenCheck,
  Wallet,
  BarChart3,
  CalendarClock,
  ShieldCheck,
  ArrowRight,
  Sparkles,
  HeartHandshake,
  Clock,
  Star,
  TrendingUp,
  Laptop,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";

const HERO_IMG = "/hero.jpg";
const ABOUT_IMG = "/about.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: "easeOut" },
  }),
};

const services = [
  {
    icon: BookOpenCheck,
    title: "Monthly Bookkeeping",
    desc: "Accurate, up-to-date books every month so you always know where your business stands.",
    span: "lg:col-span-7",
    accent: "#4A5D23",
  },
  {
    icon: Wallet,
    title: "Payroll Services",
    desc: "Reliable payroll runs and filings, on time, every time.",
    span: "lg:col-span-5",
    accent: "#E27A5E",
  },
  {
    icon: BarChart3,
    title: "Financial Reporting",
    desc: "Clear monthly statements that turn numbers into decisions.",
    span: "lg:col-span-5",
    accent: "#E27A5E",
  },
  {
    icon: CalendarClock,
    title: "Tax-Ready Prep",
    desc: "Organized records and year-end packages your CPA will love.",
    span: "lg:col-span-7",
    accent: "#4A5D23",
  },
  {
    icon: ShieldCheck,
    title: "QuickBooks Setup",
    desc: "QuickBooks setup, migration, and cleanup done right.",
    span: "lg:col-span-12",
    accent: "#E27A5E",
  },
];

const whyUs = [
  {
    icon: HeartHandshake,
    title: "A real human, not a hotline",
    desc: "You work directly with us, never a rotating call center. We learn your business and treat it like our own.",
  },
  {
    icon: Clock,
    title: "Hours back in your week",
    desc: "Hand off the spreadsheets and reconciliations. Spend your time growing, not chasing receipts.",
  },
  {
    icon: TrendingUp,
    title: "Numbers you can act on",
    desc: "Plain-English reports that show what's working, what's not, and where you can grow.",
  },
];

const stats = [
  { value: "Free", label: "First consultation" },
  { value: "Certified", label: "QuickBooks ProAdvisor" },
  { value: "100%", label: "Cloud & paperless" },
];

const promises = [
  {
    icon: ShieldCheck,
    title: "Accuracy you can trust",
    desc: "Every transaction reconciled and categorized correctly, so your numbers are always reliable.",
  },
  {
    icon: HeartHandshake,
    title: "Honest, judgment-free help",
    desc: "Behind on your books or new to all of this? No problem. We meet you exactly where you are.",
  },
  {
    icon: Clock,
    title: "Responsive communication",
    desc: "Questions answered promptly and reports delivered on time, every single month.",
  },
];

export default function Landing() {
  return (
    <div id="top" className="bg-[#FAF9F6] overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <div className="lg:col-span-6">
              <motion.div
                initial="hidden"
                animate="show"
                variants={fadeUp}
                custom={0}
                className="inline-flex items-center gap-2 rounded-full bg-[#EAE7DC] border border-[#D8D3C8] px-4 py-1.5 mb-7"
              >
                <Sparkles className="h-3.5 w-3.5 text-[#4A5D23]" />
                <span className="text-xs font-semibold tracking-wide text-[#4A5D23]">
                  North Canton, Ohio
                </span>
              </motion.div>

              <motion.h1
                initial="hidden"
                animate="show"
                variants={fadeUp}
                custom={1}
                className="font-heading text-5xl sm:text-6xl tracking-tight leading-[1.02] font-medium text-[#1A2421]"
              >
                Balanced books,
                <br />
                <span className="text-[#4A5D23]">a clearer mind.</span>
              </motion.h1>

              <motion.p
                initial="hidden"
                animate="show"
                variants={fadeUp}
                custom={2}
                className="mt-6 text-lg leading-relaxed text-[#4A4F4D] max-w-lg"
              >
                Simply Balanced Bookkeeping gives you accurate, organized
                financials that save you time, reduce stress, and help you make
                smarter decisions with confidence, so you can focus on growing
                your business and doing what you love.
              </motion.p>

              <motion.p
                initial="hidden"
                animate="show"
                variants={fadeUp}
                custom={3}
                data-testid="hero-cta-tagline"
                className="mt-9 text-base font-semibold text-[#4A5D23]"
              >
                Contact me immediately for a free evaluation of your books.
              </motion.p>

              <motion.div
                initial="hidden"
                animate="show"
                variants={fadeUp}
                custom={4}
                className="mt-4 flex flex-col sm:flex-row gap-4"
              >
                <a
                  href="https://calendly.com/julierankinsbb/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#4A5D23] px-8 py-3.5 text-sm font-semibold text-white hover:bg-[#3A4D13] transition-all duration-300 hover:shadow-lg hover:shadow-[#4A5D23]/20"
                >
                  Schedule Now
                  <ArrowRight className="h-4 w-4" />
                </a>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="show"
                variants={fadeUp}
                custom={4}
                className="mt-12 flex items-center gap-8"
              >
                {stats.map((s) => (
                  <div key={s.label}>
                    <p className="font-heading text-3xl font-bold text-[#1A2421]">
                      {s.value}
                    </p>
                    <p className="text-xs text-[#707573] mt-1 max-w-[7rem]">
                      {s.label}
                    </p>
                  </div>
                ))}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="lg:col-span-6 relative flex justify-center lg:justify-end"
            >
              <div className="relative w-full max-w-[300px] sm:max-w-[340px]">
                <div className="relative rounded-[1.75rem] overflow-hidden shadow-[0_20px_60px_rgba(28,35,33,0.12)]">
                  <img
                    src={HERO_IMG}
                    alt="Julie Rankin, Simply Balanced Bookkeeping"
                    className="w-full h-[360px] sm:h-[400px] object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A2421]/15 to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-[#EAE7DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5 relative"
            >
              <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(28,35,33,0.12)] border border-[#EAE7DC]">
                <img
                  src={ABOUT_IMG}
                  alt="You didn't start your business to do books — let me handle the numbers so you can focus on building the business you love"
                  className="w-full h-auto object-contain bg-white"
                />
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="lg:col-span-7"
            >
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#4A5D23] mb-4">
                About Us
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2421]">
                Local, dependable, and genuinely on your side
              </h2>
              <p className="mt-5 text-base leading-relaxed text-[#4A4F4D]">
                Simply Balanced Bookkeeping was built on a simple belief:
                business owners deserve financial clarity without the jargon or
                the stress. Based in North Canton, Ohio, we partner with
                businesses to keep their books clean, compliant, and always up
                to date.
              </p>
              <p className="mt-4 text-base leading-relaxed text-[#4A4F4D]">
                When you work with us, you get a dedicated bookkeeper who knows
                your name and your numbers, not a faceless service. We believe
                great bookkeeping is the quiet foundation that lets your
                business grow with confidence.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  "Cloud-based & paperless",
                  "Transparent flat-rate pricing",
                  "Dedicated personal contact",
                  "QuickBooks Certified ProAdvisor",
                ].map((point) => (
                  <div key={point} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#4A5D23] text-white">
                      <Star className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm font-medium text-[#1A2421]">
                      {point}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="why" data-testid="why-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#4A5D23] mb-4">
              Why Simply Balanced
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2421]">
              Bookkeeping that feels like a relief, not a chore
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whyUs.map((w, i) => (
              <motion.div
                key={w.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-[#D8D3C8] bg-[#FAF9F6] p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                data-testid={`why-card-${i}`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#4A5D23] text-white mb-6">
                  <w.icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-xl font-medium text-[#1A2421] mb-3">
                  {w.title}
                </h3>
                <p className="text-base leading-relaxed text-[#4A4F4D]">
                  {w.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DIVIDER */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="h-px bg-gradient-to-r from-transparent via-[#D8D3C8] to-transparent" />
      </div>

      {/* SERVICES */}
      <section id="services" data-testid="services-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#4A5D23] mb-4">
              What We Do
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2421]">
              Full-service bookkeeping, tailored to your business
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[#4A4F4D]">
              Whether you're just starting out or scaling up, we handle the
              numbers so you can focus on the work that matters.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className={`group bg-white rounded-2xl border border-[#D8D3C8] p-8 shadow-[0_4px_24px_rgba(28,35,33,0.04)] hover:-translate-y-1 hover:shadow-xl transition-all duration-300 ${s.span}`}
                data-testid={`service-card-${i}`}
              >
                <span
                  className="flex h-12 w-12 items-center justify-center rounded-xl mb-6 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${s.accent}1A`, color: s.accent }}
                >
                  <s.icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-xl font-medium text-[#1A2421] mb-2.5">
                  {s.title}
                </h3>
                <p className="text-base leading-relaxed text-[#4A4F4D]">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="relative py-20 md:py-32 bg-[#DCE5CE] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.05] bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/texture.jpg')",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#B85C3E] mb-4">
              Our Promise
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2B361A]">
              What you can expect when we work together
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {promises.map((p, i) => (
              <motion.div
                key={p.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl bg-white/70 border border-[#C4D0AE] backdrop-blur-sm p-8"
                data-testid={`promise-${i}`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E27A5E] text-white mb-6">
                  <p.icon className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-xl font-medium text-[#2B361A] mb-3">
                  {p.title}
                </h3>
                <p className="text-[#4A4F4D] leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
